// src/features/providers/types/index.ts

import { z } from 'zod';

// Provider Type interface
export interface ProviderType {
    id: number;
    name: 'Streaming Services' | 'TV Providers';
    created_at: string;
}

// Provider interface
export interface Provider {
    id: number;
    name: string;
    type_id: number;
    has_sequence_numbers: boolean;
    logo_url?: string | null;
    logo_filename?: string | null;
    website_url?: string | null;
    support_email?: string | null;
    support_phone?: string | null;
    free_trial_days?: number | null;
    student_discount_available?: boolean | null;
    currency?: string | null;
    created_at: string;
    updated_at: string;
}

// Provider with type relation
export interface ProviderWithType extends Provider {
    type: ProviderType;
}

// Common currencies for providers
export const CURRENCIES = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
    { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
    { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
    { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' }
] as const;

// Zod schema for form validation
export const providerSchema = z.object({
    name: z.string()
        .min(1, 'Provider name is required')
        .max(100, 'Provider name must be less than 100 characters'),
    type_id: z.number({ error: 'Provider type is required' }),
    has_sequence_numbers: z.boolean().optional(),
    logo_url: z.string().url().optional().nullable(),
    logo_filename: z.string().optional().nullable(),
    website_url: z.string().url('Must be a valid URL').optional().nullable(),
    support_email: z.string().email('Must be a valid email').optional().nullable(),
    support_phone: z.string().max(20, 'Phone number must be less than 20 characters').optional().nullable(),
    free_trial_days: z.number().int().min(0, 'Free trial days must be 0 or greater').max(365, 'Free trial days must be less than 365').optional().nullable(),
    currency: z.string().length(3, 'Currency must be a 3-letter code').optional().nullable()
});

export type CreateProviderInput = z.infer<typeof providerSchema>;
export type UpdateProviderInput = Partial<CreateProviderInput>;